// IFRS 18 / Cash Flow card V2 — the per-account cash-flow settings on the FS mapping screen (FsMapping.jsx is over 1,500 lines:
// this component is mounted there with one line). Saved through PATCH /coa/{id}/cf-settings (backend/cash_flow_settings.py);
// read by the cash flow (backend/cash_flow_items.py):
//   cf_cash_management — a bank overdraft (FS 3211) is part of cash management: netted into cash and cash equivalents (default yes)
//   cf_derivative_use  — a derivative account (FS 1113 / 1213 / 3207): investing (default) / held for trading / hedge of a financing,
//                        operating or investing item (PR 46 review CF8: a hedge follows the hedged item)
//   cf_discontinued    — the account belongs to the discontinued operation (the note of its own cash flows)
import React, { useMemo, useState } from "react";
import api, { toErrMsg } from "@/lib/api";
import { asArray } from "@/lib/asArray";
import { useI18n } from "@/contexts/I18nContext";
import { toast } from "sonner";
import PlCategorySettings from "@/components/PlCategorySettings";   // Batch 34 · P6-F7

const DERIV_FS = ["1113", "1213", "3207"];
export const DERIV_USES = ["investing", "trading", "financing_hedge", "operating_hedge", "investing_hedge"];

export const SETTINGS_TEXT = {
  en: {
    title: "Cash-flow settings", show: "Show", hide: "Hide",
    overdraft: "Bank overdrafts (FS 3211)",
    cashMgmt: "Part of cash management — netted into cash and cash equivalents (otherwise a financing cash flow)",
    deriv: "Derivatives (FS 1113 / 1213 / 3207) — how settlements are classified",
    use_investing: "Investing (default)", use_trading: "Held for trading — operating", use_financing_hedge: "Hedge of a financing item — financing",
    use_operating_hedge: "Hedge of an operating item — operating", use_investing_hedge: "Hedge of an investing item — investing",
    disc: "Accounts of the discontinued operation",
    discHelp: "Tag the division's accounts: the cash flow shows a note of its own operating, investing and financing cash flows.",
    filter: "Find an account (code or name)", tagged: "tagged", saved: "Saved", failed: "Could not save the cash-flow setting",
    none: "No account matches.",
  },
  ar: {
    title: "إعدادات التدفقات النقدية", show: "عرض", hide: "إخفاء",
    overdraft: "السحب على المكشوف (البند 3211)",
    cashMgmt: "جزء من إدارة النقد — يُطرح من النقد وما في حكمه (وإلا فهو تدفق نقدي تمويلي)",
    deriv: "المشتقات (البنود 1113 / 1213 / 3207) — تصنيف التسويات",
    use_investing: "استثمارية (الافتراضي)", use_trading: "محتفظ بها للمتاجرة — تشغيلية", use_financing_hedge: "تحوط لبند تمويلي — تمويلية",
    use_operating_hedge: "تحوط لبند تشغيلي — تشغيلية", use_investing_hedge: "تحوط لبند استثماري — استثمارية",
    disc: "حسابات العملية المتوقفة",
    discHelp: "حدِّد حسابات القطاع: تعرض قائمة التدفقات النقدية إيضاحًا بتدفقاته التشغيلية والاستثمارية والتمويلية.",
    filter: "ابحث عن حساب (الرمز أو الاسم)", tagged: "محدد", saved: "تم الحفظ", failed: "تعذّر حفظ إعداد التدفقات النقدية",
    none: "لا يوجد حساب مطابق.",
  },
};

export default function CashFlowAccountSettings({ coa, onSaved }) {
  const { lang } = useI18n();
  const T = (k) => (SETTINGS_TEXT[lang] || SETTINGS_TEXT.en)[k] || SETTINGS_TEXT.en[k];
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState("");
  const rows = asArray(coa);
  const ods = rows.filter((r) => (r.fs_mapping || "").trim() === "3211");
  const derivs = rows.filter((r) => DERIV_FS.includes((r.fs_mapping || "").trim()));
  const discList = useMemo(() => {
    const s = q.trim().toLowerCase();
    const hit = rows.filter((r) => r.cf_discontinued
      || (s && `${r.raw_account_code || ""} ${r.raw_account_name || ""}`.toLowerCase().includes(s)));
    return hit.slice(0, 50);
  }, [rows, q]);

  const save = async (row, body) => {
    setBusy(row.id);
    try {
      await api.patch(`/coa/${row.id}/cf-settings`, body);
      toast.success(T("saved"));
      if (onSaved) await onSaved();
    } catch (e) {
      toast.error(toErrMsg(e, T("failed")));
    } finally {
      setBusy("");
    }
  };
  const name = (r) => `${r.raw_account_code || ""} ${r.raw_account_name || ""}`.trim();

  return (
    <div className="rounded-md border p-3 text-sm" data-testid="cf-account-settings">
      <div className="flex items-center justify-between">
        <div className="font-medium">{T("title")}</div>
        <button type="button" className="text-xs underline" onClick={() => setOpen(!open)} data-testid="cf-settings-toggle">
          {open ? T("hide") : T("show")}
        </button>
      </div>
      {open && (
        <div className="mt-2 space-y-3">
          {ods.length > 0 && (
            <div data-testid="cf-settings-overdraft">
              <div className="text-xs font-semibold">{T("overdraft")}</div>
              {ods.map((r) => (
                <label key={r.id} className="flex items-center gap-2 py-0.5">
                  <input type="checkbox" checked={r.cf_cash_management !== false} disabled={busy === r.id}
                         onChange={(e) => save(r, { cf_cash_management: e.target.checked })} data-testid={`cf-cm-${r.id}`} />
                  <span className="font-mono text-[11px]">{name(r)}</span>
                  <span className="text-[11px] text-muted-foreground">{T("cashMgmt")}</span>
                </label>
              ))}
            </div>
          )}
          {derivs.length > 0 && (
            <div data-testid="cf-settings-deriv">
              <div className="text-xs font-semibold">{T("deriv")}</div>
              {derivs.map((r) => (
                <label key={r.id} className="flex items-center gap-2 py-0.5">
                  <span className="font-mono text-[11px]">{name(r)}</span>
                  <select className="rounded border px-1 py-0.5 text-xs" value={r.cf_derivative_use || "investing"} disabled={busy === r.id}
                          onChange={(e) => save(r, { cf_derivative_use: e.target.value })} data-testid={`cf-deriv-${r.id}`}>
                    {DERIV_USES.map((u) => <option key={u} value={u}>{T(`use_${u}`)}</option>)}
                  </select>
                </label>
              ))}
            </div>
          )}
          <div data-testid="cf-settings-disc">
            <div className="text-xs font-semibold">{T("disc")}</div>
            <div className="text-[11px] text-muted-foreground">{T("discHelp")}</div>
            <input className="mt-1 w-full rounded border px-2 py-1 text-xs" placeholder={T("filter")} value={q}
                   onChange={(e) => setQ(e.target.value)} data-testid="cf-disc-filter" />
            {discList.length === 0 && q.trim() ? <div className="text-[11px] text-muted-foreground">{T("none")}</div> : null}
            {discList.map((r) => (
              <label key={r.id} className="flex items-center gap-2 py-0.5">
                <input type="checkbox" checked={!!r.cf_discontinued} disabled={busy === r.id}
                       onChange={(e) => save(r, { cf_discontinued: e.target.checked })} data-testid={`cf-disc-${r.id}`} />
                <span className="font-mono text-[11px]">{name(r)}</span>
                {r.cf_discontinued ? <span className="text-[10px] text-emerald-700">{T("tagged")}</span> : null}
              </label>
            ))}
          </div>
          <PlCategorySettings rows={rows} lang={lang} busy={busy} save={save} />
        </div>
      )}
    </div>
  );
}
